<template>
	<Dialog
		:dialog-visible="dialogVisible"
		title="Switch Organization"
		message=""
		:agree-text="isSwitchingOrganization ? 'Switching' : 'Switch'"
		:agree-loading="isSwitchingOrganization"
		:disable-agree-button="!selectedOrganizationId || selectedOrganizationId === currentOrganizationId"
		:custom-agree-icon="canConfirm ? 'icon-building-exit' : ''"
		@cancel="closeDialog"
		@agree="confirmSwitchOrganization">
		<template #dialog-body>
			<div class="d-flex flex-column ga-3">
				<div class="text-body-2 text-medium-emphasis">Pick which organization you'd like to use.</div>
				<v-list class="pa-0 bg-transparent">
					<v-list-item
						v-for="listedOrganization in organizations"
						:key="listedOrganization.id"
						v-test="`org-option-${listedOrganization.id}`"
						rounded="lg"
						class="org-option-item mb-2 cursor-pointer border-sm"
						:class="{
							'org-option-item-active': selectedOrganizationId === listedOrganization.id,
							'org-option-item-current': currentOrganizationId === listedOrganization.id
						}"
						@click="selectedOrganizationId = listedOrganization.id">
						<div class="d-flex align-center justify-space-between ga-3 w-100">
							<div class="d-flex align-center ga-3 flex-grow-1 min-width-0">
								<v-avatar size="40" color="primary" variant="tonal" class="org-avatar flex-shrink-0">
									<span class="text-subtitle-1 font-weight-bold">{{ getInitials(listedOrganization.name) }}</span>
								</v-avatar>
								<div class="d-flex flex-column min-width-0">
									<div class="text-body-1 font-weight-bold text-wrap org-option-name">
										{{ listedOrganization.name }}
									</div>
									<v-chip
										v-if="currentOrganizationId === listedOrganization.id"
										size="x-small"
										color="primary"
										variant="tonal"
										class="mt-1 align-self-start">
										Current
									</v-chip>
								</div>
							</div>
							<v-icon
								v-if="selectedOrganizationId === listedOrganization.id && currentOrganizationId !== listedOrganization.id"
								v-test="'org-option-selected-icon'"
								color="primary"
								size="24"
								class="flex-shrink-0">
								icon-check-circle
							</v-icon>
						</div>
					</v-list-item>
				</v-list>
			</div>
		</template>
	</Dialog>
</template>

<script setup lang="ts">
	import { computed, ref, watch } from "vue";
	import { useToast } from "vue-toastification";
	import { useOrgSwitch } from "../composables/useOrgSwitch";
	import Dialog from "./Dialog.vue";
	import { couldNot } from "@/helpers/userMessages";

	type SwitchableOrganization = {
		id: string;
		name: string;
	};

	const props = withDefaults(
		defineProps<{
			modelValue: boolean;
			organizations: SwitchableOrganization[];
			currentOrganizationId?: string;
		}>(),
		{
			currentOrganizationId: undefined
		}
	);

	const emit = defineEmits<{
		(e: "update:modelValue", value: boolean): void;
	}>();

	const selectedOrganizationId = ref("");
	const isSwitchingOrganization = ref(false);
	const orgSwitch = useOrgSwitch();
	const toast = useToast();

	const dialogVisible = computed<boolean>({
		get: () => props.modelValue,
		set: (value) => emit("update:modelValue", value)
	});

	const canConfirm = computed(() => !!selectedOrganizationId.value && selectedOrganizationId.value !== props.currentOrganizationId);

	watch(
		() => props.modelValue,
		(isOpen) => {
			if (!isOpen) {
				return;
			}
			selectedOrganizationId.value = props.currentOrganizationId || "";
		}
	);

	function getInitials(name: string): string {
		const words = name.trim().split(/\s+/).filter(Boolean);
		if (words.length === 0) return "?";
		if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
		return (words[0][0] + words[words.length - 1][0]).toUpperCase();
	}

	function closeDialog(): void {
		dialogVisible.value = false;
	}

	async function confirmSwitchOrganization(): Promise<void> {
		if (!selectedOrganizationId.value) {
			return;
		}
		if (selectedOrganizationId.value === props.currentOrganizationId) {
			closeDialog();
			return;
		}

		closeDialog();
		await switchToOrganization(selectedOrganizationId.value);
	}

	async function switchToOrganization(organizationId: string): Promise<void> {
		isSwitchingOrganization.value = true;
		try {
			const ok = await orgSwitch.switchOrganization(organizationId);
			if (!ok) {
				toast.error(couldNot("switch organizations"));
			}
		} finally {
			isSwitchingOrganization.value = false;
		}
	}
</script>

<style scoped>
	.org-option-item {
		transition:
			border-color 0.15s ease,
			box-shadow 0.15s ease,
			background-color 0.15s ease;
		border: 1px solid rgba(var(--v-theme-ink), 0.15);
	}

	.org-option-item:hover {
		border-color: rgb(var(--v-theme-primary));
		box-shadow: inset 0 0 0 1px rgb(var(--v-theme-primary));
		background-color: #e7f1fd;
	}

	.org-option-item-active {
		border-color: rgb(var(--v-theme-primary));
		border-width: 2px;
		box-shadow: inset 0 0 0 1px rgb(var(--v-theme-primary));
		background-color: #e7f1fd;
	}

	.org-option-item-current {
		background-color: rgba(var(--v-theme-primary), 0.04);
	}

	.org-option-name {
		min-width: 0;
		overflow-wrap: anywhere;
	}

	.min-width-0 {
		min-width: 0;
	}

	.org-avatar {
		font-size: 0.875rem;
	}
</style>
